import { useCallback, useMemo } from 'react';

import { useAppConfigQuery } from '@/services/app';
import { useCurrentUserQuery } from '@/services/user';

export type Permission =
  | 'user'
  | 'user:full'
  | 'user:admin'
  | 'games:admin'
  | 'game-documents:admin'
  | 'internal-review:admin'
  | 'user:remove'
  | 'admin'
  | 'invoices'
  | 'internal-review'
  | 'tests'
  | 'tests:admin'
  | 'video'
  | 'evaluation'
  | 'documents'
  | 'documents:admin'
  | 'league-groups'
  | 'leagues:full'
  | 'trip-ranking'
  | 'trip-mail'
  | 'expenses'
  | 'balance'
  | 'posts:admin'
  | 'expenses:report'
  | 'schedule:full'
  | 'internal-videos:view'
  | 'internal-videos:admin'
  | 'internal-documents:view'
  | 'internal-documents:admin'
  | 'availability:admin'
  | 'journal:export'
  | 'availability';

const useCheckPermission = () => {
  const appConfigQuery = useAppConfigQuery();
  const currentUserQuery = useCurrentUserQuery();

  const user = currentUserQuery.data;
  const appConfig = appConfigQuery.data;

  const currentPermissions: (Permission | undefined)[] = useMemo(() => {
    let fullScheduleVisible =
      appConfig?.features.appointments.fullyVisible || user?.permissions.includes('schedule:full');

    if (user && appConfig?.features.appointments.restrictFullVisibilityForUsers.includes(user.id)) {
      fullScheduleVisible = false;
    }

    const fullLeaguesEnabled = appConfig?.features.league.fullList ? true : !!user?.is_admin;

    const userAdminEnabled = user?.is_admin || user?.permissions.includes('user:admin');
    const fullUsersEnabled = appConfig?.features.user.fullList ? true : userAdminEnabled;

    const gamesAdminEnabled = user?.is_admin ? true : user?.permissions.includes('games:admin');

    const testAdminEnabled =
      appConfig?.features.tests.enabled && (user?.is_admin || user?.permissions.includes('tests:admin'));

    const documentsAdminEnabled =
      appConfig?.features.documents && (user?.is_admin || user?.permissions.includes('documents:admin'));

    const availabilityAdminEnabled =
      appConfig?.features.user.availability && (user?.is_admin || user?.permissions.includes('availability:admin'));

    const postAdminEnabled = user?.is_admin || user?.permissions.includes('posts:admin');
    const journalExportEnabled = user?.is_admin || user?.permissions.includes('journal:export');

    const gameDocumentsAdminEnabled =
      appConfig?.features.game.gameDocuments && (user?.is_admin || user?.permissions.includes('game-documents:admin'));

    const internalReviewsAdminEnabled =
      appConfig?.features.internalReview && (user?.is_admin || user?.permissions.includes('internal-review:admin'));

    return [
      user ? 'user' : undefined,
      user?.is_admin ? 'admin' : undefined,
      appConfig?.features.user.remove ? 'user:remove' : undefined,
      appConfig?.features.invoices ? 'invoices' : undefined,
      appConfig?.features.tests.enabled ? 'tests' : undefined,
      appConfig?.features.video ? 'video' : undefined,
      appConfig?.features.internalVideo && user?.permissions.includes('internal-videos:admin')
        ? 'internal-videos:admin'
        : undefined,
      appConfig?.features.internalVideo && user?.permissions.includes('internal-videos:view')
        ? 'internal-videos:view'
        : undefined,

      appConfig?.features.internalDocument && user?.permissions.includes('internal-documents:admin')
        ? 'internal-documents:admin'
        : undefined,
      appConfig?.features.internalDocument && user?.permissions.includes('internal-documents:view')
        ? 'internal-documents:view'
        : undefined,

      appConfig?.features.game.evaluation ? 'evaluation' : undefined,
      appConfig?.features.documents ? 'documents' : undefined,
      appConfig?.features.user.availability ? 'availability' : undefined,
      appConfig?.features.league.smallLeague ? 'league-groups' : undefined,
      appConfig?.features.game.tripRanking ? 'trip-ranking' : undefined,
      appConfig?.features.game.tripMail ? 'trip-mail' : undefined,
      appConfig?.features.user.expensesReport ? 'expenses:report' : undefined,
      appConfig?.features.game.expenses ? 'expenses' : undefined,
      appConfig?.features.reports.balanceCalculations ? 'balance' : undefined,
      appConfig?.features.internalReview ? 'internal-review' : undefined,
      fullScheduleVisible ? 'schedule:full' : undefined,
      fullLeaguesEnabled ? 'leagues:full' : undefined,
      gamesAdminEnabled ? 'games:admin' : undefined,
      testAdminEnabled ? 'tests:admin' : undefined,
      documentsAdminEnabled ? 'documents:admin' : undefined,
      availabilityAdminEnabled ? 'availability:admin' : undefined,
      postAdminEnabled ? 'posts:admin' : undefined,
      fullUsersEnabled ? 'user:full' : undefined,
      userAdminEnabled ? 'user:admin' : undefined,
      journalExportEnabled ? 'journal:export' : undefined,
      gameDocumentsAdminEnabled ? 'game-documents:admin' : undefined,
      internalReviewsAdminEnabled ? 'internal-review:admin' : undefined,
    ];
  }, [appConfig, user]);

  const hasAll = useCallback(
    (requiredPermissions: Permission[]): boolean => {
      return requiredPermissions.every((permission) => currentPermissions.includes(permission));
    },
    [currentPermissions],
  );

  const hasAny = useCallback(
    (requiredPermissions: Permission[]): boolean => {
      return requiredPermissions.some((permission) => currentPermissions.includes(permission));
    },
    [currentPermissions],
  );

  return { hasAll, hasAny };
};

export default useCheckPermission;
